import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

const sections = [
  'National Articles',
  'Premium & Imported',
  'Accessories',
  'Fragrance & Grooming',
];

const AdminCategories = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({ name: '', section: sections[0] });
  const [selectedId, setSelectedId] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }

    const loadCategories = async () => {
      try {
        setIsFetching(true);
        const res = await apiFetch('/api/categories');
        const data = await res.json();
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsFetching(false);
      }
    };

    loadCategories();
  }, [user, navigate]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const resetForm = () => {
    setFormData({ name: '', section: sections[0] });
    setSelectedId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showMessage('error', 'Category name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const method = selectedId ? 'PUT' : 'POST';
      const url = selectedId ? `/api/categories/${selectedId}` : '/api/categories';
      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          name: formData.name,
          section: formData.section,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        showMessage('error', data.message || 'Unable to save category');
        return;
      }

      showMessage('success', selectedId ? 'Category updated successfully' : 'Category created successfully');
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error(error);
      showMessage('error', 'Something went wrong while saving category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setIsFetching(true);
      const res = await apiFetch('/api/categories');
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleEdit = (category) => {
    setSelectedId(category._id);
    setFormData({ name: category.name, section: category.section });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this category?');
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const res = await apiFetch(`/api/categories/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage('error', data.message || 'Unable to delete category');
        return;
      }
      showMessage('success', 'Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error(error);
      showMessage('error', 'Delete request failed');
    } finally {
      setDeletingId(null);
    }
  };

  const headerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '25px',
  };

  const cardStyle = {
    padding: '30px',
    background: 'var(--color-surface-1)',
    borderRadius: '14px',
    border: '1px solid var(--color-border-light, rgba(255,255,255,0.08))',
    boxShadow: '0 10px 35px var(--shadow-card, rgba(0,0,0,0.3))',
  };

  const labelStyle = { color: 'var(--color-text-secondary)', fontSize: '0.95rem' };

  return (
    <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 6px' }}>
      <div style={headerStyle}>
        <div>
          <h2 style={{ color: '#f97316', marginBottom: '10px' }}>Category Management</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.8, maxWidth: '680px' }}>
            Manage product categories for each section.
          </p>
        </div>
        <button className="btn" onClick={() => navigate('/admin/products')} style={{ height: '45px' }}>
          Manage Products
        </button>
      </div>

      <div style={cardStyle}>
        <h3 style={{ color: '#f97316', marginBottom: '18px' }}>
          {selectedId ? 'Edit Category' : 'Create Category'}
        </h3>

        {message && (
          <div style={{
            background: message.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
            color: message.type === 'error' ? '#fecaca' : '#bef264',
            border: `1px solid ${message.type === 'error' ? 'rgba(248,113,113,0.3)' : 'rgba(134,239,172,0.3)'}`,
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '20px',
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={labelStyle} htmlFor="category-name">Category Name</label>
            <input
              id="category-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter category name"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="category-section">Section</label>
            <select
              id="category-section"
              value={formData.section}
              onChange={(e) => setFormData({ ...formData, section: e.target.value })}
              style={inputStyle}
            >
              {sections.map((section) => (
                <option key={section} value={section}>{section}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '5px' }}>
            <button type="submit" className="btn" disabled={isSubmitting} style={{ minWidth: '160px' }}>
              {isSubmitting ? (selectedId ? 'Updating...' : 'Creating...') : (selectedId ? 'Update Category' : 'Create Category')}
            </button>
            {selectedId && (
              <button type="button" onClick={handleCancel} style={cancelBtnStyle}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div style={{ marginTop: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '18px' }}>
          <h3 style={{ color: '#f97316', margin: 0 }}>All Categories</h3>
          <div style={{ color: 'var(--color-text-secondary)' }}>{categories.length} categories</div>
        </div>

        {isFetching ? (
          <div style={{ padding: '30px', background: 'var(--color-surface-1)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)', color: '#f97316', textAlign: 'center' }}>Loading categories...</div>
        ) : categories.length === 0 ? (
          <div style={{ padding: '30px', background: 'var(--color-surface-1)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.06)', color: 'var(--color-text-secondary)', textAlign: 'center' }}>No categories found.</div>
        ) : (
          <div style={gridStyle}>
            {categories.map((category) => (
              <div key={category._id} style={categoryCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div>
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginBottom: '6px' }}>Section</div>
                    <div style={{ fontSize: '1rem', color: 'var(--color-text-primary)', marginBottom: '10px' }}>{category.section}</div>
                  </div>
                  <div style={badgeStyle}>{category.name}</div>
                </div>

                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginBottom: '5px' }}>Created</div>
                <div style={{ color: 'var(--color-text-primary)', marginBottom: '18px' }}>{new Date(category.createdAt).toLocaleDateString()}</div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button onClick={() => handleEdit(category)} style={editBtnStyle}>Edit</button>
                  <button onClick={() => handleDelete(category._id)} style={deleteBtnStyle} disabled={deletingId === category._id}>
                    {deletingId === category._id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: '10px',
  border: '1px solid var(--color-border-light, rgba(255,255,255,0.1))',
  background: 'var(--color-surface-2, #0f172a)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  fontSize: '1rem',
  marginTop: '8px',
};

const cancelBtnStyle = {
  background: '#3f3f46',
  color: 'var(--color-text-primary)',
  border: 'none',
  padding: '12px 18px',
  borderRadius: '10px',
  cursor: 'pointer',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '18px',
};

const categoryCardStyle = {
  padding: '24px',
  borderRadius: '14px',
  background: 'var(--color-surface-1, #16161a)',
  border: '1px solid var(--color-border-light, rgba(255,255,255,0.06))',
  boxShadow: '0 10px 30px var(--shadow-card, rgba(0,0,0,0.25))',
};

const badgeStyle = {
  padding: '8px 12px',
  borderRadius: '999px',
  background: 'rgba(249,115,22,0.12)',
  color: '#f97316',
  fontWeight: 700,
  fontSize: '0.9rem',
};

const editBtnStyle = {
  background: '#3b82f6',
  color: 'var(--color-text-primary)',
  border: 'none',
  padding: '12px 18px',
  borderRadius: '10px',
  cursor: 'pointer',
};

const deleteBtnStyle = {
  background: '#ef4444',
  color: 'var(--color-text-primary)',
  border: 'none',
  padding: '12px 18px',
  borderRadius: '10px',
  cursor: 'pointer',
};

export default AdminCategories;
